import { defineStore } from 'pinia'
import { getDepthAll, getMarketsTicker } from '~/apis/swap'
import { percent } from '@/utils/math'
import { useSwapStore } from './swap'

export const useSwapMarketStore = defineStore('swapMarket', {
  state: () => ({
    kline: {},
    trades: [],
    wsDepthes: {},
    overviews: [],
    overviewsRaw: {},
  }),
  getters: {
    ticker(s) {
      const swap = useSwapStore()
      return s.overviewsRaw[swap.symbol] ?? {}
    },
    getOverview: (s) => {
      const swap = useSwapStore()
      return (symbol = swap.symbol) => {
        return s.overviewsRaw[symbol] ?? {}
      }
    },
    getDepth: s => (step, length = 24) => {
      const depth = s.wsDepthes[step] ?? {}
      return {
        asks: Array.from(depth.asks?.values() || []).sort((a, b) => (+a[0] - +b[0])).slice(0, length),
        bids: Array.from(depth.bids?.values() || []).sort((a, b) => (+b[0] - +a[0])).slice(0, length),
        symbol: depth.symbol?.toUpperCase(),
      }
    },
  },
  actions: {
    resetData() {
      this.kline = {}
      this.trades = []
      this.wsDepthes = {}
    },
    async getMarketsTicker(tradeType = 'linearPerpetual') {
      const { success, data } = await getMarketsTicker({ tradeType })
      if (success) {
        this.overviews = data
        this.overviewsRaw = data.reduce((acc, next) => {
          acc[next.symbol] = {
            ...next,
            r: next.priceChangePercent,
            rt: percent(next.priceChangePercent),
            lp: next.lastPrice,
            a: next.volume,
            ta: next.volume,
            vol: next.quoteVolume,
            tv: next.quoteVolume,
          }
          return acc
        }, {})
      }
    },
    async getDepthAll({ tradeType = 'linearPerpetual', symbol, limit = 60 } = {}) {
      const swap = useSwapStore()
      symbol ??= swap.symbol
      const { success, data } = await getDepthAll({ tradeType, symbol, limit })
      if (success) {
        this.wsDepthes = {}
        data.forEach((i) => {
          this.handleDepth({ ...i, symbol, asks: i.asks.slice(-limit), bids: i.bids.slice(0, 100) })
        })
      }
    },
    handleDepth({ gear, asks = [], bids = [], symbol }) {
      this.wsDepthes[gear] ??= {}
      if (this.wsDepthes[gear].symbol !== symbol) {
        this.wsDepthes[gear] = { symbol }
      }
      const depth = this.wsDepthes[gear]
      depth.asks ??= new Map()
      depth.bids ??= new Map()

      asks.forEach((ask) => {
        ask = ask.strings || ask
        const [p, v] = ask
        if (+v === 0) {
          depth.asks.delete(p)
        }
        else {
          depth.asks.set(p, ask)
        }
      })

      bids.forEach((bid) => {
        bid = bid.strings || bid
        const [p, v] = bid
        if (+v === 0) {
          depth.bids.delete(p)
        }
        else {
          depth.bids.set(p, bid)
        }
      })
    },
  },
})
